import { RegisteredComponent } from "@builder.io/sdk-react";
import {
  DLD_FIELD,
  DLT_FIELD,
  LEGACY_HREF_FIELD,
  THEME_FIELD,
  TITLE_FIELD,
} from "../commonFields";

export const globalFooterBlockConfig: Omit<RegisteredComponent, "component"> = {
  name: "Global Footer",
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
    builderContext: true,
    builderComponents: true,
    builderLinkComponent: true,
  },
  models: ["page-footer"],
  inputs: [
    {
      name: "isRedesigned",
      type: "boolean",
      friendlyName: "Is Redesigned Version",
      defaultValue: false,
    },
    {
      name: "logo",
      type: "uiBlocks",
      defaultValue: [
        {
          "@type": "@builder.io/sdk:Element",
          component: {
            name: "Image",
            options: {
              alt: "SolarWinds Footer Logo",
              source: "aprimo",
              image:
                "https://p1.aprimocdn.net/solarwinds/94ce9bf7-6584-478a-bca6-b23400fa3359/SW_Logo_Web_Orange_XS_downloadPNG.png",
            },
          },
          responsiveStyles: {
            large: {
              boxSizing: "border-box",
              display: "flex",
              position: "relative",
              flexDirection: "column",
              flexShrink: "0",
              alignSelf: "center",
              marginTop: "0",
            },
          },
        },
      ],
    },
    {
      name: "description",
      type: "uiBlocks",
      defaultValue: [
        {
          "@type": "@builder.io/sdk:Element",
          component: {
            name: "Text",
            options: {
              text: {
                "@type": "@builder.io/core:LocalizedValue",
                Default:
                  "<p>SolarWinds was founded by IT professionals solving complex problems in the simplest way, and we have carried that spirit forward since 1999. We take pride in relentlessly listening to our customers to develop a deeper understanding of the challenges they face. Our digital agility solutions are built to help companies of any size accelerate business transformation today and into the future.</p>",
              },
            },
          },
          meta: {
            localizedTextInputs: ["text"],
            "transformed.text": "localized",
          },
          responsiveStyles: {
            large: {
              maxWidth: "504px",
              color: "var(--color-body-primary,#444444)",
            },
            medium: {
              maxWidth: "620px",
            },
            small: {
              maxWidth: "586px",
            },
          },
        },
      ],
    },
    {
      name: "links",
      type: "list",
      subFields: [
        {
          name: "link_category",
          type: "string",
          friendlyName: "Link Category",
          localized: true,
          required: false,
        },
        {
          name: "link",
          type: "list",
          subFields: [
            { ...TITLE_FIELD, required: false },
            { ...LEGACY_HREF_FIELD, required: false },
            { ...DLT_FIELD, required: false },
            { ...DLD_FIELD, required: false },
          ],
        },
      ],
      defaultValue: [
        {
          link_category: "Try SolarWinds",
          link: [
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Free Product Trials",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/products",
              },
              dlt: "Footer",
              dld: "Free Product Trials link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "All Tools",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/tools",
              },
              dlt: "Footer",
              dld: "All Tools link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Interactive Demos",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/interactive-demos",
              },
              dlt: "Footer",
              dld: "Interactive Demos link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Preference Center",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default:
                  "https://launch.solarwinds.com/Preference-Center-unsubscribe.html",
              },
              dlt: "Footer",
              dld: "Preference Center link",
            },
          ],
        },
        {
          link_category: "Security",
          link: [
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Trust Center",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/trust-center",
              },
              dlt: "Footer",
              dld: "Trust Center link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "GDPR Resource Center",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default:
                  "https://www.solarwinds.com/general-data-protection-regulation-core-it",
              },
              dlt: "Footer",
              dld: "GDPR Resource Center link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Secure by Design",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default:
                  "https://www.solarwinds.com/secure-by-design-resources",
              },
              dlt: "Footer",
              dld: "Secure by Design link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Security Information",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/information-security",
              },
              dlt: "Footer",
              dld: "Security Information link",
            },
          ],
        },
        {
          link_category: "For Customers",
          link: [
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Customer Portal",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://customerportal.solarwinds.com/",
              },
              dlt: "Footer",
              dld: "Customer Portal link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Support",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://support.solarwinds.com/",
              },
              dlt: "Footer",
              dld: "Support link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Documentation",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://documentation.solarwinds.com/",
              },
              dlt: "Footer",
              dld: "Documentation link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "THWACK Community",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://thwack.solarwinds.com/",
              },
              dlt: "Footer",
              dld: "THWACK Community link",
            },
          ],
        },
        {
          link_category: "About SolarWinds",
          link: [
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Company",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/company",
              },
              dlt: "Footer",
              dld: "Company link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Careers",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://jobs.solarwinds.com/",
              },
              dlt: "Footer",
              dld: "Careers link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "For Government",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default:
                  "https://www.solarwinds.com/public-sector/it-management-solutions-for-government",
              },
              dlt: "Footer",
              dld: "For Government link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "For Investors",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/company",
              },
              dlt: "Footer",
              dld: "For Investors link",
            },
          ],
        },
        {
          link_category: "Resources",
          link: [
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "SolarWinds Blog",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/blog",
              },
              dlt: "Footer",
              dld: "SolarWinds Blog link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Resource Center",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/resources",
              },
              dlt: "Footer",
              dld: "Resource Center link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Knowledge Hub",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "https://www.solarwinds.com/knowledge-hub",
              },
              dlt: "Footer",
              dld: "Knowledge Hub link",
            },
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Gartner® Magic Quadrant™ Observability",
              },
              href: {
                "@type": "@builder.io/core:LocalizedValue",
                Default:
                  "https://www.solarwinds.com/campaign/gartner-magic-quadrant-observability",
              },
              dlt: "Footer",
              dld: "Gartner® Magic Quadrant™ Observability link",
            },
          ],
        },
      ],
    },
    {
      ...THEME_FIELD,
      defaultValue: "white",
      enum: [
        {
          label: "white",
          value: "white",
        },
        {
          label: "black",
          value: "black",
        },
      ],
    },
    {
      name: "socialsText",
      friendlyName: "Social Links Text",
      type: "uiBlocks",
      showIf: (options) => options.get("isRedesigned") === true,
      defaultValue: [
        {
          "@type": "@builder.io/sdk:Element",
          component: {
            name: "Text",
            options: {
              text: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "<p>Connect with us</p>",
              },
            },
          },
          meta: {
            localizedTextInputs: ["text"],
            "transformed.text": "localized",
          },
          responsiveStyles: {
            large: {
              color: "var(--color-body-secondary, #666666)",
              fontFamily:
                'var(--font-space-mono, "Space Mono", monospace), sans-serif',
              fontSize: "var(--text-body-2xs, 0.75rem)",
              lineHeight: "var(--text-body-2xs--line-height, 1rem)",
              letterSpacing: "0.1875rem",
              textTransform: "uppercase",
              fontWeight: "700",
              width: "100%",
              textAlign: "center",
            },
            medium: {
              maxWidth: "522px",
            },
            small: {
              maxWidth: "416px",
            },
          },
        },
      ],
    },
    {
      name: "socials",
      type: "list",
      friendlyName: "Social Links",
      subFields: [
        {
          name: "link",
          type: "object",
          subFields: [
            { ...TITLE_FIELD, required: false },
            { ...LEGACY_HREF_FIELD, required: false },
            { ...DLT_FIELD, required: false },
            { ...DLD_FIELD, required: false },
          ],
        },
        {
          name: "icon",
          type: "uiBlocks",
          friendlyName: "Icon",
          defaultValue: [],
        },
      ],
      defaultValue: [
        {
          link: {
            title: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Facebook",
            },
            href: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "https://www.facebook.com/SolarWinds",
            },
            dlt: "Footer",
            dld: "Facebook social icon",
          },
          icon: [
            {
              "@type": "@builder.io/sdk:Element",
              "@version": 2,
              id: "builder-5f43f2eddd4840fcb01a9f5d474b916b",
              animations: [
                {
                  trigger: "hover",
                  animation: "custom",
                  steps: [
                    {
                      id: "eecb6a4162ce4af4be8b37bbc82674ef",
                      isStartState: false,
                      styles: {},
                      delay: 0,
                    },
                    {
                      id: "84574f5804934667a56c3bb18bc35926",
                      isStartState: false,
                      styles: {
                        color: "var(--color-swdc-orange-400, #ff6200)",
                      },
                      delay: 0,
                    },
                  ],
                  delay: 0,
                  duration: 0,
                  easing: "cubic-bezier(.37,.01,0,.98)",
                  repeat: false,
                  thresholdPercent: 0,
                },
              ],
              component: {
                name: "Icon",
                options: {
                  source: "aprimo",
                  icon: "https://p1.aprimocdn.net/solarwinds/f8a889a5-2a47-469d-8993-b3a00146f930/social-facebook_circle_Original%20file.svg",
                  color: "",
                  width: 32,
                  height: 32,
                },
              },
              responsiveStyles: {
                large: {
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  flexShrink: "0",
                  boxSizing: "border-box",
                  color: "var(--color-tab-link, #111111)",
                },
              },
            },
          ],
        },
        {
          link: {
            title: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Instagram",
            },
            href: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "https://www.instagram.com/solarwinds",
            },
            dlt: "Footer",
            dld: "Instagram social icon",
          },
          icon: [
            {
              "@type": "@builder.io/sdk:Element",
              "@version": 2,
              id: "builder-f8870743bc3e443f9bc830bb19a47a1a",
              animations: [
                {
                  trigger: "hover",
                  animation: "custom",
                  steps: [
                    {
                      id: "d01e5250849c4b6989f645d30fd86bcc",
                      isStartState: false,
                      styles: {},
                      delay: 0,
                    },
                    {
                      id: "12e41612414444d7bcad4492f966d9ba",
                      isStartState: false,
                      styles: {
                        color: "var(--color-swdc-orange-400, #ff6200)",
                      },
                      delay: 0,
                    },
                  ],
                  delay: 0,
                  duration: 0,
                  easing: "cubic-bezier(.37,.01,0,.98)",
                  repeat: false,
                  thresholdPercent: 0,
                },
              ],
              component: {
                name: "Icon",
                options: {
                  source: "aprimo",
                  icon: "https://p1.aprimocdn.net/solarwinds/303dbae3-3b78-4a88-9757-b3a00146f99f/social-instagram_circle_Original%20file.svg",
                  color: "",
                  width: 32,
                  height: 32,
                },
              },
              responsiveStyles: {
                large: {
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  flexShrink: "0",
                  boxSizing: "border-box",
                  color: "var(--color-tab-link, #111111)",
                },
              },
            },
          ],
        },
        {
          link: {
            title: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "Twitter",
            },
            href: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "https://twitter.com/solarwinds",
            },
            dlt: "Footer",
            dld: "Twitter social icon",
          },
          icon: [
            {
              "@type": "@builder.io/sdk:Element",
              "@version": 2,
              id: "builder-a7db214b0c534d32ab020af5c325c324",
              animations: [
                {
                  trigger: "hover",
                  animation: "custom",
                  steps: [
                    {
                      id: "83ad329225bb454ba1355ebbf264067c",
                      isStartState: false,
                      styles: {},
                      delay: 0,
                    },
                    {
                      id: "09ec5ab907584584a1d7d2e4eb3a2991",
                      isStartState: false,
                      styles: {
                        color: "var(--color-swdc-orange-400, #ff6200)",
                      },
                      delay: 0,
                    },
                  ],
                  delay: 0,
                  duration: 0,
                  easing: "cubic-bezier(.37,.01,0,.98)",
                  repeat: false,
                  thresholdPercent: 0,
                },
              ],
              component: {
                name: "Icon",
                options: {
                  source: "aprimo",
                  icon: "https://p1.aprimocdn.net/solarwinds/1c26d19a-0f5d-48a9-94b5-b3a00146fb60/social-x_circle_Original%20file.svg",
                  color: "",
                  width: 32,
                  height: 32,
                },
              },
              responsiveStyles: {
                large: {
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  flexShrink: "0",
                  boxSizing: "border-box",
                  color: "var(--color-tab-link, #111111)",
                },
              },
            },
          ],
        },
        {
          link: {
            title: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "YouTube",
            },
            href: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "https://www.youtube.com/user/solarwindsinc",
            },
            dlt: "Footer",
            dld: "YouTube social icon",
          },
          icon: [
            {
              "@type": "@builder.io/sdk:Element",
              "@version": 2,
              id: "builder-38edfe98dc2f4c2cb690348345434d49",
              animations: [
                {
                  trigger: "hover",
                  animation: "custom",
                  steps: [
                    {
                      id: "8be8946b66cc4f7d8161eba1de93dbb0",
                      isStartState: false,
                      styles: {},
                      delay: 0,
                    },
                    {
                      id: "3b599ac833824c0cba362f7e8845ce4a",
                      isStartState: false,
                      styles: {
                        color: "var(--color-swdc-orange-400, #ff6200)",
                      },
                      delay: 0,
                    },
                  ],
                  delay: 0,
                  duration: 0,
                  easing: "cubic-bezier(.37,.01,0,.98)",
                  repeat: false,
                  thresholdPercent: 0,
                },
              ],
              component: {
                name: "Icon",
                options: {
                  source: "aprimo",
                  icon: "https://p1.aprimocdn.net/solarwinds/944880b8-16be-44f6-bdcb-b3a00146fbcd/social-youtube_circle_Original%20file.svg",
                  color: "",
                  width: 32,
                  height: 32,
                },
              },
              responsiveStyles: {
                large: {
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  flexShrink: "0",
                  boxSizing: "border-box",
                  color: "var(--color-tab-link, #111111)",
                },
              },
            },
          ],
        },
        {
          link: {
            title: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "LinkedIn",
            },
            href: {
              "@type": "@builder.io/core:LocalizedValue",
              Default: "https://www.linkedin.com/company/solarwinds",
            },
            dlt: "Footer",
            dld: "LinkedIn social icon",
          },
          icon: [
            {
              "@type": "@builder.io/sdk:Element",
              "@version": 2,
              id: "builder-f8c369605e4844cf8bb142fb5056649d",
              animations: [
                {
                  trigger: "hover",
                  animation: "custom",
                  steps: [
                    {
                      id: "312bd5d0a5dc484f81c3304d749b5d67",
                      isStartState: false,
                      styles: {},
                      delay: 0,
                    },
                    {
                      id: "af9b335e00214e6c85e164600cc72d93",
                      isStartState: false,
                      styles: {
                        color: "var(--color-swdc-orange-400, #ff6200)",
                      },
                      delay: 0,
                    },
                  ],
                  delay: 0,
                  duration: 0,
                  easing: "cubic-bezier(.37,.01,0,.98)",
                  repeat: false,
                  thresholdPercent: 0,
                },
              ],
              component: {
                name: "Icon",
                options: {
                  source: "aprimo",
                  icon: "https://p1.aprimocdn.net/solarwinds/0fd2288e-5639-4d44-ac52-b3a00146fa32/social-linkedin_circle_Original%20file.svg",
                  color: "",
                  width: 32,
                  height: 32,
                },
              },
              responsiveStyles: {
                large: {
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  flexShrink: "0",
                  boxSizing: "border-box",
                  color: "var(--color-tab-link, #111111)",
                },
              },
            },
          ],
        },
      ],
    },
    {
      name: "cta",
      type: "uiBlocks",
      friendlyName: "CTA Button",
      defaultValue: [
        {
          "@type": "@builder.io/sdk:Element",
          component: {
            name: "Core:Button",
            options: {
              text: "Try SolarWinds",
              variant: "secondary",
              size: "m",
              href: "https://www.solarwinds.com/products",
              dld: "Try SolarWinds button",
              dlt: "Footer",
            },
          },
          responsiveStyles: {
            large: {
              marginRight: "auto",
            },
          },
        },
      ],
    },
  ],
  defaultStyles: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flexShrink: "0",
    boxSizing: "border-box",
    width: "100%",
    backgroundColor: "var(--color-background,#ffffff)",
  },
  canHaveChildren: false,
};
